"use client";

import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { usePathname } from "next/navigation";

import CustomImage from "../../atoms/customImage";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import { IS_HIDE_APP_BANNER } from "../../../constants/common/common";
import CookieUtil from "../../../utils/storage/cookieUtil";
import { getStoreAppRedirectBannerData } from "../../../utils/dynamicHomeUtils/storeAppRedirectBanner";

/**
 * Mobile homepage app redirection banner driven by the active masthead store's
 * `redirectionAppBannerImg`. Separate from {@link AppRedirectionBanner} so home-specific
 * behaviour can evolve independently of PLP/PDP/info pages.
 *
 * @param {object} props
 * @param {boolean} props.isMobile - Whether the request is on mobile
 * @param {boolean} props.isSmartBuyUser - Whether the HDFC SmartBuy partner cookie is set
 * @param {Array<object>} [props.initialStores] - SSR masthead stores from homepage feed
 * @returns {React.ReactElement|null}
 */
const HomeAppRedirectionBanner = ({ isMobile, isSmartBuyUser, initialStores = [] }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { trackEvent } = useAnalytics();
  const pathname = usePathname();
  const pageStores = useSelector((state) => state.dynamicHome?.pageStores ?? [], shallowEqual);

  const appBannerData = useMemo(() => {
    const stores = pageStores.length > 0 ? pageStores : initialStores;
    const selectedStore = stores.find((store) => store.isSelected) ?? stores[0];
    return getStoreAppRedirectBannerData(selectedStore);
  }, [initialStores, pageStores]);

  const { imageUrl, appDeepLink } = appBannerData ?? {};

  useEffect(() => {
    const isHidden = CookieUtil.getCookie(IS_HIDE_APP_BANNER) === "true";
    setIsVisible(!isHidden);
  }, []);

  /**
   * Dismisses the homepage app banner and persists the preference in a cookie.
   */
  const onCloseBanner = useCallback(() => {
    setIsVisible(false);

    trackEvent(
      cleverTapCommonEvents?.appRedirectBannerClosed,
      {
        page_name: pathname || "",
        page_type: window?.fnpPageType || "",
      },
      ["clevertap"],
    );

    CookieUtil.setCookie(IS_HIDE_APP_BANNER, "true", {
      maxAge: 60 * 60 * 24,
      path: "/",
      sameSite: "lax",
      secure: true,
    });
  }, [pathname, trackEvent]);

  if (!isMobile || isSmartBuyUser || !appBannerData || !isVisible) {
    return null;
  }

  return (
    <div id="homeAppBanner" className="relative flex w-full items-start">
      <a href={appDeepLink} aria-label="Open FNP app" className="flex min-w-0 flex-1">
        <CustomImage
          src={imageUrl}
          alt="Download FNP app"
          width={750}
          height={120}
          sizes="100vw"
          isPriority={false}
          className="h-auto w-full object-cover"
        />
      </a>

      <button
        type="button"
        onClick={onCloseBanner}
        className="absolute right-10 top-18 z-10 flex shrink-0 items-center justify-center"
        aria-label="Close app banner"
      >
        <CustomImage src="/icons/close.svg" alt="Close" width={20} height={20} isPriority={false} />
      </button>
    </div>
  );
};

HomeAppRedirectionBanner.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  isSmartBuyUser: PropTypes.bool.isRequired,
  initialStores: PropTypes.arrayOf(PropTypes.object),
};

export default React.memo(HomeAppRedirectionBanner);
